/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
'use client';

import { Chevron as IconChevron } from '@ahamove/icons';
import {
  getAddressLabels,
  type Address,
  type Path,
} from '@ahm/api-wrappers-core';
import { useLocationStore } from '@ahm/react-map/store';
import { ActionIcon, Group, Popover } from '@mantine/core';
import { X } from 'lucide-react';
import { useLocale } from 'next-intl';
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import AddPathBtn from './add-path-btn';
import EntranceToRO from './entrance-to-ro';
import FormPath from './form-path';
import PathBtn from './path-btn';
import { AddressAutocomplete } from '@/components/address-autocomplete';
import { useGetLocationFeatures } from '@/features/booking/api/service/use-get-location-features';
import {
  ActionIndexPath,
  DEFAULT_MAX_STOP_POINTS,
  MAX_STOP_POINTS_SHOW_ENTRANCE_RO,
} from '@/lib/constants';
import { fetchLocationInfo, getServiceType } from '@/lib/utils';
import { usePathname } from '@/navigation';
import { useDetectModalStore } from '@/stores';
import { useBookingStore } from '@/stores/booking/booking-store-provider';

type Props = {
  pathIndex: number;
  path?: Path;
};

// eslint-disable-next-line sonarjs/cognitive-complexity
function PathEditable({ path, pathIndex }: Props) {
  const curLocale = useLocale();
  const pathname = usePathname();

  const [shouldShowAddMore, setShouldShowAddMore] = useState(
    Boolean(pathIndex < -1 || pathIndex > 1)
  );
  const [currentPath, setCurrentPath] = useState<Path | undefined>(undefined);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [showEntranceRO, setShowEntranceRO] = useState<boolean>(false);

  const {
    paths,
    removePath,
    updatePathByIndex,
    pathFocusIndex,
    setPathFocusIndex,
    currentLocation,
  } = useLocationStore((state) => state);

  const { modal_id: modalId, clearDetectPopoverId } = useDetectModalStore(
    (state) => state
  );

  const { isLoadingStore } = useBookingStore((state) => state);

  const { isHMService, isNationwide } = useMemo(
    () => getServiceType(pathname),
    [pathname]
  );

  const { data: locationFeatures } = useGetLocationFeatures({
    enabled: isHMService,
  });

  const autocompleteInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setShouldShowAddMore(
      pathIndex === ActionIndexPath.AddMoreBtn && !currentPath
    );
  }, [pathIndex]);

  useEffect(() => {
    if (path) {
      if (pathFocusIndex && pathFocusIndex < 0) {
        setEditMode(true);
      }
      setCurrentPath(path);
    }
  }, [path]);

  useEffect(() => {
    if (shouldShowAddMore && autocompleteInputRef.current) {
      autocompleteInputRef.current.focus();
    }
  }, [shouldShowAddMore]);

  const initialAddress = useMemo(() => {
    if (!currentPath) return;

    return {
      address: currentPath.address,
      first_label: getAddressLabels(currentPath.address)[0],
      second_label: getAddressLabels(currentPath.address)[1],
      lat: currentPath.lat,
      lng: currentPath.lng,
      level: undefined,
      remarks: '',
    } as Address;
  }, [currentPath]);

  const isPinLocation = useMemo(() => {
    return Boolean(currentLocation?.address || paths?.length);
  }, [currentLocation, paths]);

  const handleChangeAddress = async (addr: Address | null) => {
    if (!addr?.lat || !addr?.lng) return;

    const lat = Number(addr.lat);
    const lng = Number(addr.lng);

    const addrData = {
      adr_source: addr.code ? `fav_${addr.code}` : addr.adr_source,
      address: addr.address,
      first_label: addr.first_label,
      second_label: addr.second_label,
      lat,
      lng,
      name: addr.contact_name ?? '',
      mobile: addr.contact_number ?? '',
      remarks: addr.address_detail ?? '',
      level: addr.level,
      polygon: addr.polygon,
      entrances: addr.entrances,
    };

    let nationwideProps = {};
    if (isNationwide) {
      const { provinceInfo, districtInfo, wardInfo } = await fetchLocationInfo(
        lat,
        lng
      );
      nationwideProps = {
        nationwide_province_id: provinceInfo?._id,
        nationwide_district_id: districtInfo?._id,
        nationwide_ward_id: wardInfo?._id,
      };
    }
    let hmPathData = {};
    if (isHMService) {
      hmPathData = {
        location_features: locationFeatures?.[0]?.code
          ? [locationFeatures[0].code]
          : [],
      };
    }
    const newPath = {
      ...path,
      ...addrData,
      ...nationwideProps,
      ...hmPathData,
    };
    updatePathByIndex(newPath, pathIndex);

    if (pathIndex !== 0 && !path) {
      setShouldShowAddMore(true);
    }
  };

  const handleFocusPath = () => {
    setPathFocusIndex(pathIndex);
  };

  const handleClearPath = () => {
    if (currentPath) {
      setCurrentPath(undefined);
      removePath(pathIndex);
      clearDetectPopoverId();
    }
    !shouldShowAddMore && setShouldShowAddMore(true);
  };

  const handleConfirmPath = (path: Path) => {
    // TODO: handle COD insurance
    // if (path.cod) {
    //   const request = selectedService?.requests?.find(
    //     (r) => r.type === 'COD_INSURANCE'
    //   );
    //   if (request) {
    //     if (path.item_value) {
    //       path.item_value = undefined;
    //     }
    //     handleChangeRequestParams(request?._id, true);
    //   }
    // }
    updatePathByIndex(path, pathIndex);
    setEditMode(false);
    setCurrentPath(undefined);
    setPathFocusIndex(null);
  };

  useEffect(() => {
    if (editMode) {
      autocompleteInputRef.current?.focus();
      clearDetectPopoverId();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editMode]);

  useEffect(() => {
    if (paths && paths.length < MAX_STOP_POINTS_SHOW_ENTRANCE_RO) {
      setShowEntranceRO(false);
    }
  }, [paths?.length]);

  const handleEnableEditMode = () => {
    if (paths && paths.length >= MAX_STOP_POINTS_SHOW_ENTRANCE_RO) {
      setShowEntranceRO(paths.length === MAX_STOP_POINTS_SHOW_ENTRANCE_RO);
    }

    if (paths && paths.length > DEFAULT_MAX_STOP_POINTS) return;
    setShouldShowAddMore(false);
    setEditMode(true);

    setTimeout(() => {
      if (autocompleteInputRef.current) {
        autocompleteInputRef.current.focus();
      }
    }, 300);
  };

  const handleChangePopover = (opened: boolean) => {
    // disable click outside while other modal is open
    if (modalId) {
      return;
    }
    if (!modalId) {
      setEditMode(opened);
    }
    if (!opened && pathIndex === ActionIndexPath.AddMoreBtn) {
      setShouldShowAddMore(true);
    }
  };

  if (shouldShowAddMore) {
    return <AddPathBtn onClick={handleEnableEditMode} />;
  }

  if (!editMode && pathIndex >= 0) {
    return (
      <PathBtn
        path={currentPath}
        pathIndex={pathIndex}
        onClick={() => setEditMode(true)}
      />
    );
  }

  return (
    <Popover
      opened={!isLoadingStore && editMode}
      onChange={handleChangePopover}
      styles={{
        dropdown: {
          filter: 'drop-shadow(0px 16px 48px rgba(0, 0, 0, 0.08))',
          borderRadius: 12,
          background: 'var(--mantine-color-neutral-0)',
          padding: 0,
        },
        arrow: {
          backgroundColor: 'white',
        },
      }}
      zIndex={200}
      width={343}
      offset={{ mainAxis: 8, crossAxis: 0 }}
      position="right"
      withArrow
      arrowSize={16}
      shadow="md"
    >
      <Popover.Target>
        <Group
          role="button"
          wrap="nowrap"
          gap={0}
          align="center"
          w="100%"
          pos="relative"
          onClick={() => {
            if (showEntranceRO) {
              setShowEntranceRO(false);
            }
            if (currentPath?.lat && currentPath.lng && !editMode) {
              setEditMode(true);
            }
          }}
        >
          <AddressAutocomplete
            pathIndex={pathIndex}
            withSavedPlace
            pointType={pathIndex === 0 ? 'pickup' : 'dropoff'}
            pointIndex={
              pathIndex && pathIndex > 0 && paths.length > 2
                ? pathIndex
                : undefined
            }
            inputProps={{
              variant: 'filled',
              color: 'neutral',
              w: '100%',
              autoFocus:
                paths.length > 1 && pathIndex === paths.length - 1
                  ? true
                  : undefined,
              onFocusCapture: (e) => {
                if (showEntranceRO) {
                  setShowEntranceRO(false);
                }
                handleFocusPath();
                e.stopPropagation();
              },
            }}
            lang={curLocale}
            onChange={handleChangeAddress}
            address={initialAddress}
            withinPortal={false}
            isPinLocation={isPinLocation}
          />
          {editMode ||
          pathIndex === ActionIndexPath.ShowInput ? undefined : pathIndex ===
            ActionIndexPath.ShowInput ? (
            <ActionIcon size="xl" variant="transparent">
              <IconChevron height={16} width={16} />
            </ActionIcon>
          ) : (
            <ActionIcon
              variant="transparent"
              size="xl"
              onClick={(e) => {
                e.stopPropagation();
                handleClearPath();
              }}
              c="neutral.4"
              title="Clear"
            >
              <X size={16} />
            </ActionIcon>
          )}
        </Group>
      </Popover.Target>

      {currentPath?.lat && currentPath.lng && editMode ? (
        <Popover.Dropdown>
          <FormPath
            onClose={() => {
              setEditMode(false);
              if (pathIndex < 0) {
                setCurrentPath(undefined);
              }
            }}
            onConfirm={handleConfirmPath}
            path={currentPath}
            pathIndex={pathIndex}
          />
        </Popover.Dropdown>
      ) : null}

      {showEntranceRO ? (
        <Popover.Dropdown>
          <EntranceToRO />
        </Popover.Dropdown>
      ) : null}
    </Popover>
  );
}

export default memo(PathEditable);
